import { Component, OnInit, OnDestroy, ViewChild, HostListener, ViewEncapsulation, ElementRef, AfterViewInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';

import { Subject } from 'rxjs';
import { take, takeUntil, filter, delay } from 'rxjs/operators';
import PerfectScrollbar from 'perfect-scrollbar';
import { ModalService } from 'app/service/modal.service';

import { CoreConfigService } from '@core/services/config.service';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';

import { AuthenticationService } from 'app/auth/service';
import { User } from 'app/auth/models';
import { NotificationsService } from 'app/layout/components/navbar/navbar-notification/notifications.service';

@Component({
  selector: 'vertical-menu',
  templateUrl: './vertical-menu.component.html',
  styleUrls: ['./vertical-menu.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VerticalMenuComponent implements OnInit, OnDestroy, AfterViewInit {
  coreConfig: any;
  menu: any;
  isCollapsed: boolean;
  isScrolled: boolean = false;
  currentUser: User;
  public notifications: any = { messages: [], systemMessages: [] };

  @ViewChild('menuContent', { read: ElementRef, static: false }) menuContentRef?: ElementRef;
  private psInstance?: PerfectScrollbar;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreMenuService} _coreMenuService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   * @param {NotificationsService} _notificationsService
   * @param {NgbModal} _modalService
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private _coreMenuService: CoreMenuService,
    private _coreSidebarService: CoreSidebarService,
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _notificationsService: NotificationsService,
    private _modalService: ModalService
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
    this._authenticationService.currentUser
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(user => {
        this.currentUser = user;
      });
  }

  get currentUserInitial(): string {
    const name = this.currentUser?.fullName || this.currentUser?.username || 'A';
    return name.charAt(0).toUpperCase();
  }

  get currentUserEmail(): string {
    return this.currentUser?.email || this.currentUser?.username || 'admin@cmc.com.vn';
  }

  openNotificationModal(modalContent: any): void {
    this._notificationsService.markAllAsRead();
    this._modalService.open(modalContent, {
      title: 'Thông báo hệ thống',
      subtitle: this.totalNotificationsCount > 0 ? `${this.totalNotificationsCount} Thông báo` : undefined,
      icon: 'bell',
      hasBodyWrapper: false,
      centered: true,
      size: 'lg',
      scrollable: true,
    });
  }

  get unreadNotificationsCount(): number {
    const unreadMsgs = this.notifications?.messages?.filter((m: any) => !m.read)?.length || 0;
    const unreadSysMsgs = this.notifications?.systemMessages?.filter((s: any) => !s.read)?.length || 0;
    return unreadMsgs + unreadSysMsgs;
  }

  get totalNotificationsCount(): number {
    const msgLen = this.notifications?.messages?.length || 0;
    const sysLen = this.notifications?.systemMessages?.length || 0;
    return msgLen + sysLen;
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On Init
   */
  ngOnInit(): void {
    this._notificationsService.onApiDataChange
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(data => {
        this.notifications = data;
      });

    // Subscribe config change
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
      this.isCollapsed = config.layout.menu.collapsed;
      setTimeout(() => {
        this.psInstance?.update();
      }, 500);
    });

    // Subscribe to the menu collapse toggled event
    this._coreMenuService.onItemCollapseToggled
      .pipe(
        delay(500),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        this.psInstance?.update();
      });

    this.isCollapsed = this._coreSidebarService.getSidebarRegistry('menu').collapsed;

    // Close the menu on router NavigationEnd (Required for small screen to close the menu on select)
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        if (this._coreSidebarService.getSidebarRegistry('menu')) {
          this._coreSidebarService.getSidebarRegistry('menu').close();
        }
      });

    // scroll to active on navigation end
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        setTimeout(() => {
          const activeEl = this.menuContentRef?.nativeElement.querySelector('.navigation .active');
          if (activeEl) {
            activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });
      });

    // Get current menu
    this._coreMenuService.onMenuChanged
      .pipe(
        filter(value => value !== null),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        this.menu = this._coreMenuService.getCurrentMenu();
      });
  }

  /**
   * On After View Init
   */
  ngAfterViewInit(): void {
    if (this.menuContentRef) {
      this.psInstance = new PerfectScrollbar(this.menuContentRef.nativeElement, {
        suppressScrollX: true,
        wheelPropagation: false
      });
    }
  }

  /**
   * On Destroy
   */
  ngOnDestroy(): void {
    this.psInstance?.destroy();
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * On Sidebar scroll set isScrolled as true
   */
  onSidebarScroll(): void {
    if ((this.menuContentRef?.nativeElement?.scrollTop || 0) > 3) {
      this.isScrolled = true;
    } else {
      this.isScrolled = false;
    }
  }

  /**
   * Toggle sidebar expanded status
   */
  toggleSidebar(): void {
    this._coreSidebarService.getSidebarRegistry('menu').toggleOpen();
  }

  /**
   * Toggle sidebar collapsed status
   */
  toggleSidebarCollapsible(): void {
    // Get the current menu state
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(config => {
        this.isCollapsed = config.layout.menu.collapsed;
      });

    if (this.isCollapsed) {
      this._coreConfigService.setConfig({ layout: { menu: { collapsed: false } } }, { emitEvent: true });
    } else {
      this._coreConfigService.setConfig({ layout: { menu: { collapsed: true } } }, { emitEvent: true });
    }
  }
}
